import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import TotpRequiredDialog from 'src/components/organisms/auth/totp-required-dialog/totp-required-dialog';

export type TotpSubmitResult =
  | { status: 'submit'; code: string }
  | { status: 'cancel' };

type TotpDialogState = {
  open: boolean;
  title?: string;
  description?: string;
  errorMessage?: string | null;
  isSubmitting: boolean;
};

type TotpDialogContextValue = {
  openDialog: (options?: { description?: string; title?: string }) => void;
  setSubmitting: (value: boolean) => void;
  setError: (message: string | null) => void;
  waitForSubmit: () => Promise<TotpSubmitResult>;
  closeDialog: () => void;
  state: TotpDialogState;
  submitCode: (code: string) => void;
  cancel: () => void;
};

const TotpDialogContext = createContext<TotpDialogContextValue | null>(null);

export function TotpDialogProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { t } = useTranslation();

  const resolverRef = useRef<((result: TotpSubmitResult) => void) | null>(null);

  const [state, setState] = useState<TotpDialogState>({
    open: false,
    title: t('common:codeIsRequiredTitle'),
    description: t('common:codeIsRequiredDescription'),
    errorMessage: null,
    isSubmitting: false,
  });

  const openDialog = useCallback(
    (options?: { title?: string; description?: string }) => {
      setState({
        open: true,
        title: options?.title ?? t('common:codeIsRequiredTitle'),
        description:
          options?.description ?? t('common:codeIsRequiredDescription'),
        errorMessage: null,
        isSubmitting: false,
      });
    },
    [t],
  );

  const setSubmitting = useCallback((value: boolean) => {
    setState(prev => ({ ...prev, isSubmitting: value }));
  }, []);

  const setError = useCallback((message: string | null) => {
    setState(prev => ({ ...prev, errorMessage: message }));
  }, []);

  const closeDialog = useCallback(() => {
    setState(prev => ({
      ...prev,
      open: false,
      errorMessage: null,
      isSubmitting: false,
    }));
  }, []);

  const waitForSubmit = useCallback(() => {
    return new Promise<TotpSubmitResult>(resolve => {
      resolverRef.current = resolve;
    });
  }, []);

  //do not close the dialog after submit! wait for potential totp errors
  const submitCode = useCallback((code: string) => {
    resolverRef.current?.({ status: 'submit', code });
    resolverRef.current = null;
  }, []);

  const cancel = useCallback(() => {
    resolverRef.current?.({ status: 'cancel' });
    resolverRef.current = null;

    setState(prev => ({
      ...prev,
      open: false,
      errorMessage: null,
      isSubmitting: false,
    }));
  }, []);

  const value = useMemo<TotpDialogContextValue>(
    () => ({
      openDialog,
      setSubmitting,
      waitForSubmit,
      setError,
      closeDialog,
      state,
      submitCode,
      cancel,
    }),
    [
      openDialog,
      setSubmitting,
      setError,
      closeDialog,
      state,
      submitCode,
      cancel,
      waitForSubmit,
    ],
  );

  return (
    <TotpDialogContext.Provider value={value}>
      {children}
      <TotpRequiredDialog />
    </TotpDialogContext.Provider>
  );
}

export function useTotpDialog() {
  const ctx = useContext(TotpDialogContext);
  if (!ctx) {
    throw new Error('useTotpDialog must be used inside TotpDialogProvider');
  }

  return ctx;
}
